/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Card, Pagination } from '@nexigendigital/nxui'
import { useEmailLogsQuery } from 'api/account'
import { setEmailLog } from 'store/slices/accountSlice'
import { EmailLog } from 'models/account'
import { parseISO } from 'helpers/utils'

import ViewEmailLightbox from 'containers/account/lightbox/viewEmailLightbox'

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const StyledEmailLogCard = styled(Card)`
    width: 100%;
    margin-bottom: 0;
    border-radius: 0 0 16px 16px;

    header {
        border-radius: 0;
    }
`

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function EmailLogs() {
    const dispatch = useDispatch()
    const [currentPage, setCurrentPage] = useState(1)
    const [lightboxStatus, setLightboxStatus] = useState(false)
    const { data: emailLogs, isLoading, isSuccess: isEmailLogsSuccess } = useEmailLogsQuery(currentPage)
    const emailLogsData = useMemo(() => {
        if (isEmailLogsSuccess && emailLogs) {
            return [
                ...emailLogs.data.map(({ id, date, subject }: EmailLog) => {
                    const parseDate = parseISO(date).setLocale('en-AU').toLocaleString()
                    return {
                        subject,
                        date: parseDate,
                        action: (
                            <Anchor
                                color={'primary'}
                                onClick={() => {
                                    dispatch(setEmailLog({ id, parseDate, subject }))
                                    setLightboxStatus(true)
                                }}
                            >
                                View
                            </Anchor>
                        )
                    }
                })
            ]
        }

        return []
    }, [isEmailLogsSuccess, emailLogs])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <>
            <StyledEmailLogCard
                table={{
                    conditions: {
                        loading: isLoading && 'Loading Email Logs...',
                        header: false,
                        search: false,
                        pagination: false
                    },
                    render: {
                        columns: () => [
                            {
                                Header: '',
                                accessor: 'date'
                            },
                            {
                                Header: '',
                                accessor: 'subject'
                            },
                            {
                                Header: '',
                                accessor: 'action'
                            }
                        ],
                        data: () => emailLogsData
                    }
                }}
                title={'Email Log'}
                footer={(
                    <Pagination
                        currentPage={emailLogs?.meta.current_page || 1}
                        totalCount={emailLogs?.meta.total || 1}
                        pageSize={emailLogs?.meta.per_page || 15}
                        onPageChange={(page: number) => setCurrentPage(page)}
                    />
                )}
            />
            <ViewEmailLightbox status={lightboxStatus} close={() => setLightboxStatus(false)}/>
        </>
    )
}
